/**
 * What `GET /debug_token` said about a Meta system user token.
 *
 * ## WHY THIS IS ITS OWN MODULE AS OF SLICE 40
 *
 * It lived in `integration.ts` from slice 13, where it had exactly one consumer
 * and belonged. Slice 40 adds a second Meta integration kind — `meta_ads` — and
 * `debug_token` answers identically for both, so `meta-ads.ts` wants the same
 * shape.
 *
 * Importing it across gave a real import cycle rather than a stylistic one:
 * `meta-ads.ts` needs this schema, and `integration.ts` needs `meta-ads.ts` for
 * the `meta_ads` arm of `integrationSchema` and of the `config` and
 * `last_check` unions. Two zod modules in a cycle do not merely look untidy —
 * `z.object({...})` runs at module evaluation, so whichever side loads second
 * reads `undefined` for the other's exports and the error arrives as a
 * `Cannot read properties of undefined` from inside zod, at import time, with
 * nothing in the stack naming the cycle.
 *
 * The alternative was a second near-identical token shape on the ads side. That
 * is worse than a cycle: it is a second place for the `expires_at: 0` decision
 * below to be re-made, and it would be re-made differently — the WhatsApp side
 * has three screens depending on `introspected` telling "never expires" apart
 * from "would not say", and a parallel shape starts with none of them.
 *
 * Everything outside this package imports from the `@wend/shared` barrel, so
 * the move is invisible to all of them: not one import statement in `apps/`
 * changed.
 *
 * ## SECURITY
 *
 * `scopes` holds permission names, never the token. `appId` identifies the
 * tenant's own app and is not secret. **There is no field here for the token or
 * any fragment of it, and there must never be one.** The database agrees:
 * `integrations_last_check_has_no_secret` checks the *inside* of this object
 * for every spelling a bearer credential could arrive under, precisely because
 * this shape is the one thing on a check snapshot that sits next to a token.
 */
import { z } from 'zod';

import { isoDateTimeSchema } from '../common.js';

/**
 * What `GET /debug_token` said about the token itself.
 *
 * This is why `integration_status` has an `expiring` member that slice 3 could
 * never write: nothing in the phone number, WABA or ad account response says
 * when a token stops working, and an expired one simply starts answering
 * `OAuthException` — a support ticket that could have been a banner three weeks
 * earlier. For `meta_ads` the same expiry stops Wend **pausing a campaign that
 * is spending money**, which is the same mechanism with a bill attached.
 */
export const metaTokenInfoSchema = z
  .object({
    /**
     * Whether `debug_token` actually answered.
     *
     * It is the one call in a check allowed to fail without failing the check.
     * When this is `false`, `expiresAt: null` means "not reported", **not**
     * "never expires", and a screen must say which of the two it is rather than
     * painting a green "permanent token".
     *
     * Slice 40 leans on this a second way: `metaAdsGrant` reports
     * `known: false` when this is false, so an unintrospected token grants no
     * permission to spend rather than being drawn as having none.
     */
    introspected: z.boolean(),
    /** When the token stops working — or null for one that never does. */
    expiresAt: isoDateTimeSchema.nullable(),
    valid: z.boolean(),
    /**
     * Permission names, e.g. `whatsapp_business_messaging`, `ads_management`.
     * Never the token.
     */
    scopes: z.array(z.string()),
    appId: z.string().nullable(),
  })
  .strict();
export type MetaTokenInfo = z.infer<typeof metaTokenInfoSchema>;
